/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { mergeMap, take, tap, exhaustMap, first, timeout, takeUntil } from 'rxjs/operators';
import { LoggingService, Logger } from 'crimson';
import { PlaytestLiveService } from '../core/services/playtest-live.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';

import { CdkScrollable } from '@angular/cdk/scrolling';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatAnchor, MatButton } from '@angular/material/button';
import { Subject, interval } from 'rxjs';
import { OffDeviceLinkDirective, HyperLinkUserActivationGuardDirective } from 'hig';

@Component({
    selector: 'nv-playtest-launch',
    templateUrl: './playtest-launch.component.html',
    styleUrls: ['./playtest-launch.component.scss'],
    imports: [
        MatDialogTitle,
        CdkScrollable,
        MatDialogContent,
        FlexModule,
        MatDialogActions,
        MatAnchor,
        OffDeviceLinkDirective,
        HyperLinkUserActivationGuardDirective,
        MatButton
    ]
})
export class PlaytestLaunchComponent implements OnInit, OnDestroy {
    private logger: Logger;
    private destroy$: Subject<boolean> = new Subject<boolean>();
    state = '';
    nativeClientDeeplink = '';
    browserclientDeeplink = '';
    launchSessionId = '';

    constructor(
        private playtestLiveService: PlaytestLiveService,
        private loggingService: LoggingService,
        private sanitizer: DomSanitizer,
        private dialog: MatDialogRef<PlaytestLaunchComponent>
    ) {
        this.logger = this.loggingService.getLogger('LaunchComponent');
        this.state = 'waiting';

        this.playtestLiveService
            .getPageState()
            .pipe(
                take(1),
                mergeMap(() =>
                    interval(1000).pipe(
                        exhaustMap(() =>
                            this.playtestLiveService.addCheckCapacity(this.playtestLiveService.getLaunchTestId())
                        ),
                        first(res => res?.capacityOk === 'good'),
                        timeout(30000)
                    )
                ),
                tap(() => (this.state = 'good')),
                mergeMap(() => {
                    this.playtestLiveService.incrementTestRunCount(this.playtestLiveService.getLaunchTestId());
                    return this.playtestLiveService.getLaunchUrls(this.playtestLiveService.getLaunchTestId());
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(
                result => {
                    this.nativeClientDeeplink = result?.nativeLink;
                    this.browserclientDeeplink = result?.webLink;
                },
                err => {
                    this.state = 'error';
                    this.logger.info('error: ' + err);
                }
            );
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    getSanitizedUrl(clientType: string): SafeUrl {
        if (clientType === 'native' && this.nativeClientDeeplink !== 'error') {
            return this.sanitizer.bypassSecurityTrustUrl(this.nativeClientDeeplink);
        } else if (clientType === 'browser' && this.browserclientDeeplink !== 'error') {
            return this.sanitizer.bypassSecurityTrustUrl(this.browserclientDeeplink);
        }
        return null;
    }

    onLaunchClick() {
        this.dialog.close();
    }

    ngOnInit(): void {}
}
